/** @format */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import awsconfig from "../../config/awsconfig";
import "./BackendStatusBanner.scss";

const API_URL = awsconfig.REACT_APP_PUBLIC_API || "";
const CHECK_INTERVAL_MS = 60000;
const REQUEST_TIMEOUT_MS = 8000;

function useApiHealthCheck() {
  const [isDown, setIsDown] = useState(false);
  const intervalRef = useRef(null);

  const check = useCallback(async () => {
    if (!API_URL) return;
    try {
      const response = await axios.get(API_URL, {
        timeout: REQUEST_TIMEOUT_MS,
        validateStatus: () => true,
      });
      setIsDown(response.status !== 200);
    } catch (_) {
      setIsDown(true);
    }
  }, []);

  useEffect(() => {
    check();
    intervalRef.current = setInterval(check, CHECK_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [check]);

  return { isDown, retry: check };
}

export function BackendStatusBanner() {
  const { t } = useTranslation("translation");
  const { isDown, retry } = useApiHealthCheck();

  if (!isDown) return null;

  const message =
    t("backend_status.message", {
      defaultValue:
        "Something is wrong with the backend. Some features may not work. Please try again later.",
    }) ||
    "Something is wrong with the backend. Some features may not work. Please try again later.";
  const checkAgain =
    t("backend_status.check_again", { defaultValue: "Check again" }) ||
    "Check again";

  return (
    <div className="backend-status-banner" role="alert">
      <div className="backend-status-banner__inner">
        <span className="backend-status-banner__text">{message}</span>
        <button
          type="button"
          className="backend-status-banner__retry"
          onClick={retry}
        >
          {checkAgain}
        </button>
      </div>
    </div>
  );
}
